<!--
  Component: MetadataBase

  This is a base class for components that are used in the tabs of the SQS that
  are populated based on metadata.  This includes both components containing
  metric values, as well as other content without data (metadata only, e.g.
  headings).

  From mixin:
  - isCalculatingLayout - flag to toggle hidden method for CSS, to allow proper
      calculation of element sizes

  Props:
  - metadata - array of metadata rows (one for each component slot, which may
      be "", the default slot) for this specific component, should be passed in
      from parent component
-->

<template>
  <div class="metadata-base">{{mainDisplayText}} - {{schoolValue}}</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Component, { mixins } from 'vue-class-component'

import { NS_CURRENT_REPORT } from '../const'
import { valIsMissing, valIsNA } from '@/utils/data'

import { LayoutMixin } from '@/mixin'

/* Sample Slot Name many subclasses may use */
const SN_SCHOOL = 'school_value'

@Component({
  props: {
    metadata: Array
  },
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'isSchoolDataLoaded',
      'valueForMetric'
    ])
  }
})
export default class MetadataBase extends mixins(LayoutMixin) {
  /* Getters */

  get defaultValue () { return this.dispValueForSlotName('') }
  get schoolValue () { return this.dispValueForSlotName(SN_SCHOOL) }

  get hasSchoolSlot () { return this.hasSlotName(SN_SCHOOL) }

  // Main slot is the one with no specified name, or default to school value
  get mainDisplayText () {
    return this.displayTextForSlotName('') || this.displayTextForSlotName(SN_SCHOOL)
  }

  /* Methods - utility */

  slotDataForName (slotName) {
    return this.metadata.filter(d => d.component_slot === slotName)
  }
  hasSlotName (slotName) {
    return this.slotDataForName(slotName).length > 0
  }
  hasValueForSlotName (slotName) {
    return !valIsMissing(this.valueForSlotName(slotName))
  }
  hasNAForSlotName (slotName) {
    return valIsNA(this.valueForSlotName(slotName))
  }
  // Raw value from store, may be undefined, null, empty string
  valueForSlotName (slotName) {
    const slotData = this.slotDataForName(slotName)
    if (slotData.length === 0) {
      return undefined
    } else if (slotData.length > 1) {
      console.log(`Warning: valueForSlotName ${slotName} has multiple entries, returning first`)
      console.log(slotData)
    }
    const colName = slotData[0].value_colname
    return this.valueForMetric(colName)
  }
  dispValueForSlotName (slotName) {
    if (!this.isSchoolDataLoaded) return ''
    const value = this.valueForSlotName(slotName)
    return valIsMissing(value) ? '' : value
  }
  numValueForSlotName (slotName) {
    const value = this.valueForSlotName(slotName)
    return valIsMissing(value) ? undefined : +value
  }
  displayTextForSlotName (slotName) {
    const slotData = this.slotDataForName(slotName)
    return slotData.length > 0 ? slotData[0].display_text : ''
  }

  // Prefix-based arrays of slotData
  slotDataForPrefix (slotNamePrefix) {
    return this.metadata.filter(d => d.component_slot.indexOf(slotNamePrefix) === 0)
  }
  hasValueForSlot (slotData) {
    return !valIsMissing(this.valueForSlot(slotData))
  }
  hasNAForSlot (slotData) {
    return valIsNA(this.valueForSlot(slotData))
  }
  valueForSlot (slotData) {
    return this.valueForSlotName(slotData.component_slot)
  }
  numValueForSlot (slotData) {
    return this.numValueForSlotName(slotData.component_slot)
  }
  dispValueForSlot (slotData) {
    return this.dispValueForSlotName(slotData.component_slot)
  }
  displayTextForSlot (slotData) {
    return this.displayTextForSlotName(slotData.component_slot)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/sqs/2019/tab-mods/MetadataBase.vue